import type { IQuote } from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuoteApi } from '../api';

interface IUseFetchQuoteReturnValue {
  errorType: string;
  fetch: () => void;
  isLoading: boolean;
  quote?: IQuote;
}

export const useFetchQuote = (id?: string): IUseFetchQuoteReturnValue => {
  const quoteApi = useQuoteApi();
  const [quote, setQuote] = useState<IQuote | undefined>();
  const ref = useRef(false);
  const [state, setState] = useState({ isLoading: false, errorType: '' });

  const fetchQuote = useCallback(() => {
    if (id && !ref.current) {
      ref.current = true;
      setState({ isLoading: true, errorType: '' });

      void (async (): Promise<void> => {
        try {
          const res = await quoteApi.get(id);
          setQuote(res);
          setState({ isLoading: false, errorType: '' });
        } catch (error) {
          let errorType = '';
          const errorCode = (error as Error).message;

          switch (errorCode) {
            case '410':
              errorType = 'Quote Expired';
              break;
            case '404':
              errorType = 'Not Found';
              break;
            case '401':
              errorType = 'Unauthorized';
              break;
          }

          setState({ isLoading: false, errorType: errorType });
        } finally {
          ref.current = false;
        }
      })();
    }
  }, [id, quoteApi]);

  useEffect(fetchQuote, [fetchQuote]);

  return { quote, ...state, fetch: () => fetchQuote() };
};
